// Modo "Ambiente completo" — pantalla propia (ruta /ambiente-completo).
//
// 2 a 4 productos de la marca en UNA sola foto de ambiente: el aparador, la mesa ratona y
// la lámpara armando un living, en vez de tres packshots sueltos. Sólo para marcas del
// rubro Hogar y Decoración — `soloHogarDeco` en `modesCatalog` esconde la card, y esta
// pantalla además rebota a la home si alguien llega por URL.
// Ver ADR-2026-09-03-modo-ambiente-completo.
//
// POR QUÉ ES UN MODO Y NO UNA VISTA DEL CATÁLOGO: el Catálogo de muebles genera una
// imagen POR PRODUCTO (producto × vista). Acá los 2-4 productos entran en la MISMA foto.
// Es la operación inversa, y no hay forma de expresarla con el contador del Catálogo.
//
// Toma la anatomía del modo Modelo v2 (ADR-2026-08-21-modelo-v2-una-sola-columna), la
// misma que estrenó "Mostrar cómo funciona"
// (ADR-2026-09-03-modo-mostrar-como-funciona-en-el-shell-nuevo):
//   • una COLUMNA de configuración a la izquierda, con las cards de 76px, el pie fijo
//     con los ajustes y el Generar, y el ancho elástico `ANCHO_PANEL_CONFIG`;
//   • lo que no se resuelve en la columna se elige en el PANEL LATERAL, que se abre
//     sobre el área de la derecha (productos, ambiente, formato);
//   • a la derecha, la muestra del modo hasta que exista una tanda, y las tandas después.
//
// Dos pasos sobre la misma pantalla: (1) elegir los productos —la grilla adentro de la
// columna, como Automático—, (2) configurar la escena. La columna es la misma en los
// dos: avanzar agrega lo que falta debajo de la card de productos y cambia el CTA.
//
// CONECTADO al backend (ADR-2026-09-27-ambiente-completo-conectado): el Generar manda el
// pedido a `POST /v1/generate/ambiente-completo` (`ambienteCompletoApi.ts`). En desktop la
// tanda cae en la MISMA columna de resultados que Animar imagen y Carrusel
// (`useColumnaResultadosModo`); en mobile, en la pantalla de resultados del modo.
//
// EN MOBILE son las MISMAS dos decisiones en dos pantallas full-screen, con la anatomía
// de Modelo (ADR-2026-08-24-modelo-v2-mobile-segunda-pantalla): (1) la grilla de
// productos con su CTA al pie, (2) "Armar la escena" con las mismas cards de la columna
// y la barra de ajustes + Generar abajo (`BarraInferiorMobile`, la misma de Modelo). El
// panel lateral no cruza —no hay ancho para una capa al costado—: el ambiente y el
// formato se eligen en un bottom sheet, y "Editar la escena" devuelve al paso 1.
// Ver ADR-2026-09-10-ambiente-completo-en-el-telefono.
//
// OJO con los colores: los tokens `brand-*` no declaran `<alpha-value>`, así que
// `text-brand-black/90` no emite regla. Acá se usa `text-black/<n>` para los grises y
// `text-brand-black` pelado para el negro.

import { SkuDelProducto } from '../../../components/SkuDelProducto';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type LucideIcon,
  BedDouble,
  BookOpen,
  ChevronRight,
  Image as ImageIcon,
  PencilLine,
  Sofa,
  Sparkles,
  Star,
  Sun,
  Upload,
  UtensilsCrossed,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../../utils/preloadGeneratedImages';
import { formatSlugToAspect } from '../agentGenerate';
import { pedirAmbienteCompleto } from '../ambienteCompletoApi';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import { useSidebar } from '../../../context/SidebarContext';
import { useShellEscritorio } from '../../../hooks/useShellEscritorio';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { esRubroHogarDeco } from '../../../lib/rubro';
import CachedImg from '../../../components/CachedImg';
import { EncabezadoPantalla } from '../../../components/PantallaChrome';
import type { MockProductV2 } from '../mockData';
import { getFormatLabel } from '../mockData';
import { fetchUserProducts } from '../productsApi';
import {
  ProductEditContext,
  ProductsContext,
  ProductsLoadedContext,
} from '../productsContext';
import { CardProductos } from '../modelo/PanelCompacto';
import { PickProductPanelModelo } from '../modelo/PickProductPanelModelo';
import { SubirProductoDrawer } from '../components/SubirProductoDrawer';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { SheetLateral } from '../../../components/SheetLateral';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { BarraInferiorMobile } from '../modelo/BarraInferiorMobile';
import { FormatPicker, FormatPickerGrid } from '../FormatPicker';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
// La subida de un fondo propio y la card de "Mis fondos" son las MISMAS de Modelo
// (ADR-2026-09-28-ambiente-completo-fondo-propio).
import { CardFondo, SubirFondoView } from '../components/SubirFondoView';
import { fetchFondosGuardados, type FondoGuardado } from '../fondosApi';
import { ContadorImagenes } from '../components/ContadorImagenes';
import {
  CalidadSegmentada,
  CeldaAjuste,
  ValorConChevron,
  CALIDAD_DEFAULT,
  type CalidadImagen,
} from '../components/AjustesRapidosProducto';
import { AnimatedBlurBg } from '../AnimatedBlurBg';
import { ANCHO_PANEL_CONFIG, FILA_MODO_REDISENO } from '../components/layoutModo';
import { nombreCortoModo } from '../modesCatalog';
import {
  AMBIENTES,
  AMBIENTE_FONDO,
  AMBIENTE_FONDO_ID,
  AMBIENTE_PROPIO,
  AMBIENTE_PROPIO_ID,
  ESCENAS_DEFAULT,
  MAX_ESCENAS,
  MAX_PRODUCTOS,
  MIN_PRODUCTOS,
  ROLES_PRODUCTO,
  ambienteDelPedido,
  ambientePorId,
  conProtagonista,
  costoEscenas,
  faltaDelAmbiente,
  ratioDeFormato,
  rolPorId,
  rolesDeSeleccion,
  type AmbienteDeEscena,
  type AmbienteId,
  type RolProducto,
} from '../ambienteCompleto';

/** Segmento de ruta del modo — el mismo que `path` en `modesCatalog`. */
const PATH_MODO = 'ambiente-completo';

// El ancho del panel y la sangría de la fila salen de `layoutModo`, que es donde viven
// para los modos del rediseño. Este modo tenía su propia copia y se había quedado en el
// valor VIEJO —660px contra los 600 de hoy
// (ADR-2026-09-06-un-solo-ancho-de-panel-para-los-modos)—, así que abría con un panel más
// gordo que Automática (ADR-2026-09-24-variantes-usa-la-caja-de-los-otros-modos).

/** Ancho del panel lateral — el de los pickers de Modelo (`ANCHO_PANEL_LATERAL`). */
const ANCHO_PANEL_LATERAL = 615;

/**
 * Aire entre la columna de configuración y el panel lateral, en px.
 *
 * El mismo que en "Variantes de acabado" y "Mostrar cómo funciona"
 * (ADR-2026-09-04-el-panel-lateral-se-separa-de-la-columna): pegado al borde de la
 * columna, las dos cajas blancas se leían como una sola y el panel parecía montado
 * ENCIMA de la columna en vez de al lado.
 */
const SEPARACION_PANEL = 24;

/** Columnas de las grillas del panel lateral, como en Modelo. */
const COLUMNAS_PANEL_LATERAL = 3;

/**
 * Qué hace el modo, en una línea y una bajada.
 *
 * Lo dicen las DOS superficies: la muestra de la columna de la derecha en desktop y la
 * pantalla de presentación de mobile. Es la misma frase a propósito — quien lo ve en la
 * compu y después en el teléfono tiene que reconocer el modo.
 */
const MUESTRA_TITULO = 'Varios productos, una sola foto';
const MUESTRA_BAJADA =
  'Atlas arma el ambiente y ubica cada mueble según el papel que le des';

/**
 * La bajada de la tarjeta "Lo describo yo".
 *
 * El nombre sale del dominio compartido con "Mostrar cómo funciona", pero la bajada NO:
 * allá la pregunta es "dónde PASA" el video y acá es cómo ES el ambiente de una foto. El
 * id y el nombre siguen siendo los mismos, que es lo que importa que no se bifurque.
 */
const BAJADA_AMBIENTE_PROPIO = 'Escribí vos cómo es el ambiente y con qué luz.';

/** Qué se está eligiendo con el ambiente — la misma línea en el panel y en el sheet. */
const BAJADA_AMBIENTE =
  'El lugar donde se arma la escena. La luz y lo que hay alrededor salen de acá.';

/** Lo que dice la card de la columna cuando el fondo propio está elegido pero sin foto. */
const VALOR_FONDO_SIN_FOTO = 'Subí una foto del lugar';

/**
 * Formato por defecto: **Editorial (3:2)**, horizontal, y no el `DEFAULT_FORMAT_ID`
 * cuadrado del resto de la app. Un ambiente es ancho —los muebles se reparten a lo largo
 * de la pared— y en 1:1 la escena entra recortada o los productos quedan diminutos. Es el
 * único modo donde el encuadre lo pide el contenido y no la red social de destino.
 */
const FORMATO_DEFAULT = 'editorial';

/**
 * Portada del modo mientras no hay tanda. Cuadro PROPIO: un living armado (sillón, mesa
 * baja con libros, alfombra, aplique) — varios productos en la misma escena, que es lo
 * que el modo promete. Antes era prestada del Catálogo del rubro (el ropero en contexto),
 * un solo producto y de otro modo.
 */
const PORTADA = '/images/modo_ambiente_completo/portada.png';

/**
 * Cuánto tarda, estimado, una tanda (ms): el paceo de la barra del overlay de mobile y
 * de los recuadros de la columna. Una escena es una composición con hasta 10 fotos de
 * referencia (30–90 s) y las N van en paralelo. El corte real lo da la respuesta
 * (`completeNow`); esto sólo decide a qué velocidad avanza la barra mientras tanto.
 */
const DURACION_GENERACION_MS = 90_000;

/** Cuántas miniaturas entran en el abanico de la card de productos vacía. */
const MINIATURAS_CARD_VACIA = 2;

/**
 * El FALLBACK de las miniaturas de la card vacía: dos packshots de muebles que ya están
 * en el repo (los de las vistas del Catálogo), en vez de la remera/el jean/la zapatilla
 * de Modelo. Se usan sólo mientras el catálogo carga o si la cuenta todavía no tiene
 * productos; con productos, las miniaturas son los ÚLTIMOS que subió el usuario
 * (ver `miniaturasVacio`).
 */
const PLACEHOLDERS_MUEBLES = [
  '/images/vistas_muebles/packshot.png',
  '/images/modo_catalogo_muebles/hero_packshot.png',
];

/**
 * Los pasos del modo.
 *
 * `presentacion` es SÓLO de mobile: la pantalla que explica qué hace el modo antes de
 * pedir nada —la muestra arriba y la card de productos abajo
 * (ADR-2026-09-10-ambiente-completo-mobile-abre-con-la-card-de-productos).
 * En desktop no existe: ahí la muestra vive en la columna de la derecha y se ve AL MISMO
 * TIEMPO que la grilla, así que una pantalla previa sería la misma información dos veces.
 */
// `resultados` es SÓLO de mobile: en desktop las escenas aterrizan en la columna de la
// derecha, que está al lado de la configuración y no la tapa
// (ADR-2026-09-10-ambiente-completo-mobile-genera-a-pantalla-completa).
type Paso = 'presentacion' | 'producto' | 'configurar' | 'resultados';
type PanelLateral = 'productos' | 'ambiente' | 'formato' | null;

/**
 * Una tanda de MOBILE: lo que salió de un toque de Generar. En desktop las tandas viven en
 * la columna de resultados compartida (`useColumnaResultadosModo`) y no pasan por acá.
 */
interface TandaEscena {
  id: string;
  cantidad: number;
  ratio: number;
  /** Generando hasta que el backend responde; lista con sus imágenes; o fallida. */
  estado: 'generando' | 'lista' | 'fallida';
  /** Las escenas que salieron (puede ser menos que `cantidad`: una fallida no se muestra). */
  imagenes: PreloadedImage[];
  /**
   * Cuántas escenas ENTREGÓ el backend (los ids que devolvió), antes de la precarga. Es lo
   * que dice el encabezado y lo que decide si la tanda fue parcial: una escena que falló
   * no se cobró y hay que decirlo, no fingir que salieron todas
   * (ADR-2026-09-28-ambiente-completo-review-greptile-150).
   */
  entregadas?: number;
  /** Por qué falló, ya mostrable. */
  error?: string;
  /**
   * Cómo se llama el ambiente con el que salió — el nombre del curado, o el texto que
   * escribió el usuario. Se guarda RESUELTO y no como id: con el ambiente propio no hay
   * id que buscar, y una tanda vieja tiene que seguir diciendo con qué se generó aunque
   * después se cambie el ambiente.
   */
  ambienteEtiqueta: string;
  productos: number;
  creadaEn: number;
}

/** Ícono de cada ambiente, hasta que lleguen las fotos (ver `AMBIENTES`). */
const ICONO_AMBIENTE: Record<AmbienteId, LucideIcon> = {
  living: Sofa,
  comedor: UtensilsCrossed,
  dormitorio: BedDouble,
  balcon: Sun,
  estudio: BookOpen,
};

/**
 * Card en FILA de 76px —miniatura cuadrada, etiqueta y valor, chevron— la misma forma que
 * las cards de Modelo, Pose y Fondo de la columna de Modelo v2
 * (ADR-2026-08-31-modelo-v2-cards-en-fila). Local y no importada por lo mismo que en
 * "Mostrar cómo funciona": la de `PanelCompacto` no se exporta y trae la lógica de
 * mosaico de modelos que acá no aplica.
 */
const CardFila: React.FC<{
  etiqueta: string;
  valor: string;
  miniatura: React.ReactNode;
  onClick: () => void;
  /** Resaltada: es la card cuyo panel está abierto. */
  activa?: boolean;
}> = ({ etiqueta, valor, miniatura, onClick, activa = false }) => (
  <button
    type="button"
    data-abre-panel-lateral
    onClick={onClick}
    // Activa = SÓLO el fondo teñido, sin anillo: es el mismo dibujo que en "Variantes de
    // acabado" y "Mostrar cómo funciona", y el `ring-2` acá le daba a la card un borde
    // azul de foco que ninguna otra card del shell tiene.
    //
    // El fondo va con hex+alpha, NO `bg-brand-primary/[0.06]`: `brand-primary` es una
    // `var()` con hex y el modificador de opacidad de Tailwind no emite regla (ADR-140).
    className={`w-full h-[76px] shrink-0 rounded-2xl p-3 flex items-center gap-3 text-left transition-colors ${
      activa
        ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)]'
        : 'bg-black/[0.035] hover:bg-black/[0.06]'
    }`}
  >
    <div className="relative h-full aspect-square shrink-0 rounded-xl overflow-hidden bg-white grid place-items-center">
      {miniatura}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-bold text-brand-black leading-tight">{etiqueta}</p>
      <p className="text-[13px] text-black/50 leading-tight mt-0.5 truncate">{valor}</p>
    </div>
    <ChevronRight
      className={`w-[18px] h-[18px] shrink-0 ${activa ? 'text-brand-primary' : 'text-black/30'}`}
    />
  </button>
);

/**
 * Recuadro "generando" de la tanda: el mismo de `PanelTandaCreaciones` (que no lo
 * exporta), con el fondo animado con el que la app ya dice "esto lo está haciendo la IA".
 */
const RecuadroGenerando: React.FC<{ ratio: number; etiqueta: string }> = ({
  ratio,
  etiqueta,
}) => (
  <div
    className="relative overflow-hidden rounded-2xl bg-brand-primary"
    style={{ aspectRatio: String(ratio) }}
  >
    <AnimatedBlurBg selected />
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-4 text-white">
      <Sparkles className="w-7 h-7 drop-shadow" strokeWidth={1.5} />
      <span className="text-[13px] font-semibold drop-shadow text-center">{etiqueta}</span>
      <span className="relative h-1 w-2/3 overflow-hidden rounded-full bg-white/30">
        <span className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-white animate-tanda-barra" />
      </span>
    </div>
  </div>
);

/**
 * La grilla de ambientes: una tarjeta por lugar, con su foto (o su ícono mientras no la
 * haya), su nombre y la línea que dice qué luz y qué cosas trae.
 *
 * Componente y no un bloque suelto porque la eligen DOS superficies con distinta
 * densidad: el panel lateral de desktop (tres columnas) y el bottom sheet de mobile
 * (dos). Es la misma decisión y tiene que verse igual en las dos.
 */
const GrillaAmbientes: React.FC<{
  ambienteId: AmbienteDeEscena;
  onElegir: (id: AmbienteId) => void;
  /** La última: el ambiente lo escribe el usuario. NO cierra el panel (ver el comentario). */
  onElegirPropio: () => void;
  /** La primera: una foto propia del lugar. Abre la subida, sin cerrar el panel. */
  onSubirFondo: () => void;
  columnas: number;
}> = ({ ambienteId, onElegir, onElegirPropio, onSubirFondo, columnas }) => (
  <div
    className="grid gap-3"
    style={{ gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}
  >
    {/* PRIMERA: una FOTO propia del lugar —lo primero que se ofrece es traer lo tuyo, como
        en el panel de fondos de Modelo (ADR-2026-09-02-subir-y-describir-primero)—. La
        misma card punteada que "Lo describo yo", y con el mismo comportamiento que "Subir un
        fondo" en el panel de Modelo: tocarla abre la subida adentro del panel, y el fondo
        que sale de ahí queda elegido (ADR-2026-09-28-ambiente-completo-fondo-propio).
        Marcada mientras el ambiente elegido sea un fondo propio, se haya subido recién o
        elegido de "Mis fondos". */}
    <button
      type="button"
      aria-pressed={ambienteId === AMBIENTE_FONDO_ID}
      onClick={onSubirFondo}
      className={`relative flex flex-col text-left rounded-2xl overflow-hidden transition-shadow ${
        ambienteId === AMBIENTE_FONDO_ID
          ? 'bg-brand-primary/[0.04] ring-2 ring-brand-primary'
          : 'bg-brand-primary/[0.02] ring-[1.5px] ring-dashed ring-brand-primary/35 hover:ring-brand-primary/60'
      }`}
    >
      <div className="relative aspect-[4/3] grid place-items-center">
        <span className="w-10 h-10 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
          <Upload className="w-[18px] h-[18px]" />
        </span>
        {ambienteId === AMBIENTE_FONDO_ID && (
          <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white grid place-items-center shadow-md ring-2 ring-white">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m5 12 5 5L20 7" />
            </svg>
          </span>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="text-[13.5px] font-semibold text-brand-primary leading-tight">
          {AMBIENTE_FONDO.nombre}
        </p>
        <p className="text-[12px] text-black/50 leading-snug mt-0.5">
          {AMBIENTE_FONDO.descripcion}
        </p>
      </div>
    </button>

    {AMBIENTES.map((a) => {
      const activa = a.id === ambienteId;
      const Icono = ICONO_AMBIENTE[a.id];
      return (
        <button
          key={a.id}
          type="button"
          aria-pressed={activa}
          onClick={() => onElegir(a.id)}
          className={`relative flex flex-col text-left rounded-2xl overflow-hidden bg-neutral-50 transition-shadow ${
            activa
              ? 'ring-2 ring-brand-primary'
              : 'ring-1 ring-black/[0.06] hover:ring-black/[0.14]'
          }`}
        >
          <div className="relative aspect-[4/3] bg-neutral-100 grid place-items-center">
            {a.imageUrl ? (
              <CachedImg
                src={a.imageUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <Icono className="w-8 h-8 text-black/35" strokeWidth={1.5} />
            )}
            {activa && (
              <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white grid place-items-center shadow-md ring-2 ring-white">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m5 12 5 5L20 7" />
                </svg>
              </span>
            )}
          </div>
          <div className="px-3 py-2.5">
            <p className="text-[13.5px] font-semibold text-brand-black leading-tight">
              {a.nombre}
            </p>
            <p className="text-[12px] text-black/50 leading-snug mt-0.5">
              {a.descripcion}
            </p>
          </div>
        </button>
      );
    })}

    {/* La última: describirlo a mano. La misma tarjeta que las curadas —para que se lea
        como una opción más de la misma decisión y no como un escape—, pero punteada y en
        azul, que es como la app viene marcando "esto lo ponés vos". Es la misma que
        estrenó "Dónde pasa" en el modo de al lado
        (ADR-2026-09-04-donde-pasa-tiene-una-opcion-para-describirlo).

        NO cierra el panel al elegirla: elegir uno de los cinco curados termina la
        decisión, pero acá recién empieza —hay que escribir—, y cerrar dejaría al usuario
        mirando una card que dice "Escribí cómo es el ambiente" sin dónde hacerlo. */}
    <button
      type="button"
      aria-pressed={ambienteId === AMBIENTE_PROPIO_ID}
      onClick={onElegirPropio}
      className={`relative flex flex-col text-left rounded-2xl overflow-hidden transition-shadow ${
        ambienteId === AMBIENTE_PROPIO_ID
          ? 'bg-brand-primary/[0.04] ring-2 ring-brand-primary'
          : 'bg-brand-primary/[0.02] ring-[1.5px] ring-dashed ring-brand-primary/35 hover:ring-brand-primary/60'
      }`}
    >
      <div className="relative aspect-[4/3] grid place-items-center">
        <span className="w-10 h-10 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
          <PencilLine className="w-[18px] h-[18px]" />
        </span>
        {ambienteId === AMBIENTE_PROPIO_ID && (
          <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white grid place-items-center shadow-md ring-2 ring-white">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m5 12 5 5L20 7" />
            </svg>
          </span>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="text-[13.5px] font-semibold text-brand-primary leading-tight">
          {AMBIENTE_PROPIO.nombre}
        </p>
        <p className="text-[12px] text-black/50 leading-snug mt-0.5">
          {BAJADA_AMBIENTE_PROPIO}
        </p>
      </div>
    </button>

  </div>
);

/**
 * Las tandas generadas, una sección por toque de Generar.
 *
 * `maxColumnas` es lo único que cambia entre superficies: la columna de la derecha en
 * desktop entra tres escenas por fila; el teléfono, dos.
 */
const TandasEscenas: React.FC<{
  tandas: TandaEscena[];
  maxColumnas: number;
}> = ({ tandas, maxColumnas }) => (
  <>
    {tandas.map((t) => {
      // Lista: se cuentan las que ENTREGÓ el backend, no las pedidas; mientras genera o si
      // falló, las pedidas (que es lo que los recuadros dibujan).
      const salieron = t.estado === 'lista' ? (t.entregadas ?? t.imagenes.length) : t.cantidad;
      const parcial = t.estado === 'lista' && salieron < t.cantidad;
      // Entregadas por el backend (y cobradas) que la precarga no pudo firmar o cargar: se
      // ven menos fotos que las que dice el encabezado, y hay que decir dónde están.
      const sinCargar = t.estado === 'lista' ? Math.max(0, salieron - t.imagenes.length) : 0;
      return (
      <section key={t.id} className="flex flex-col gap-2.5">
        <h4 className="text-[13px] font-semibold leading-none text-black/75">
          {salieron} {salieron === 1 ? 'escena' : 'escenas'}
          <span className="mx-[7px] inline-block h-[3px] w-[3px] rounded-full bg-[#3C3C43]/35 align-middle" />
          {/* La etiqueta puede ser un texto largo —el ambiente descrito a mano—, así que
              se recorta en vez de partir el encabezado en dos renglones. */}
          <span className="font-medium text-[#3C3C43]/50 inline-block max-w-[220px] truncate align-bottom">
            {t.ambienteEtiqueta}
          </span>
          <span className="font-medium text-[#3C3C43]/50"> · {t.productos} productos</span>
        </h4>
        {/* Una sola escena va grande; de dos para arriba, en grilla. Es lo mismo que
            hacen las tandas del resto de la app: la cantidad decide el tamaño, no un
            ancho fijo. */}
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${Math.min(t.cantidad, maxColumnas)}, minmax(0, 1fr))`,
            maxWidth: t.cantidad === 1 ? 560 : undefined,
          }}
        >
          {/* Los recuadros latiendo SÓLO mientras genera: una tanda fallida ya terminó y
              dejarlos prometería algo que no va a llegar (review de Greptile, PR #151). */}
          {t.estado === 'lista' &&
            t.imagenes.map((img) => (
              <CachedImg
                key={String(img.id)}
                src={img.optSignedUrl || img.signedUrl}
                alt=""
                className="w-full rounded-2xl object-cover bg-black/[0.04]"
                style={{ aspectRatio: String(t.ratio) }}
              />
            ))}
          {t.estado === 'generando' &&
            Array.from({ length: t.cantidad }).map((_, i) => (
              <RecuadroGenerando key={i} ratio={t.ratio} etiqueta="Armando el ambiente…" />
            ))}
        </div>
        {/* La entrega PARCIAL se dice: sin esto, dos fotos bajo un título que decía "2
            escenas" no se distinguían de tres pedidas y dos salidas. Las que faltan no se
            cobraron (el débito es al entregar). */}
        {parcial && (
          <p className="text-[12.5px] leading-snug text-black/55 px-1">
            {avisoDeEntregaParcial(salieron, t.cantidad)}
          </p>
        )}
        {/* Las que existen pero no se pudieron cargar acá: mismo texto que la tanda entera
            sin cargar (review de Greptile, PR #150, segunda vuelta). */}
        {sinCargar > 0 && (
          <p className="text-[12.5px] leading-snug text-black/55 px-1">
            {sinCargar === 1
              ? 'Una escena se generó pero no pudimos cargarla acá. La encontrás en Creaciones.'
              : `${sinCargar} escenas se generaron pero no pudimos cargarlas acá. Las encontrás en Creaciones.`}
          </p>
        )}
        {t.estado === 'fallida' && (
          <p className="rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
            {t.error ?? 'No se pudieron generar las escenas. Intentá nuevamente.'}
          </p>
        )}
      </section>
      );
    })}
  </>
);

/**
 * El aviso de una tanda que entregó menos escenas que las pedidas. En desktop va debajo del
 * Generar (la columna compartida dibuja sólo lo que salió y no tiene dónde decirlo); en
 * mobile, al pie de la tanda.
 */
const avisoDeEntregaParcial = (salieron: number, pedidas: number): string => {
  const faltan = pedidas - salieron;
  return `Salieron ${salieron} de ${pedidas} escenas: ${
    faltan === 1 ? 'la que falta falló y no se cobró' : 'las que faltan fallaron y no se cobraron'
  }.`;
};

/**
 * Una fila de "Quién manda en la foto": miniatura + nombre + la pastilla del rol.
 *
 * Reusa el dibujo de "Qué lleva cada modelo" (`QuienLlevaQueV2` en `PanelCompacto`), que
 * es el bloque que la app ya tiene para "una fila por integrante del grupo". No se
 * importa porque aquél muestra productos por modelo y acá el dato de la derecha es el
 * rol, que además se cambia desde la fila.
 *
 * Tocar la fila abre el menú de los tres roles. El protagonista NO puede degradarse solo
 * —la escena quedaría sin nadie al frente—: se cambia ascendiendo a otro, y por eso sus
 * dos opciones restantes están apagadas con la explicación abajo.
 */
const FilaRol: React.FC<{
  producto: MockProductV2;
  rol: RolProducto;
  abierto: boolean;
  onToggle: () => void;
  onElegir: (rol: RolProducto) => void;
}> = ({ producto, rol, abierto, onToggle, onElegir }) => {
  const esProtagonista = rol === 'protagonista';
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={abierto}
        className={`w-full flex items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors ${
          abierto ? 'bg-white' : 'hover:bg-white'
        }`}
      >
        <span className="w-9 h-9 rounded-lg overflow-hidden bg-white grid place-items-center shrink-0 ring-1 ring-black/[0.07]">
          <CachedImg
            src={producto.imageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] text-brand-black truncate">{producto.name}</span>
          <SkuDelProducto sku={producto.sku} />
        </span>
        {/* La pastilla del protagonista va en AZUL y con la estrella; las otras dos en
            gris. Es la única jerarquía que importa leer de un vistazo: quién está
            adelante. Apoyo y acento se distinguen por el texto, no por el color, porque
            entre ellos no hay orden. */}
        <span
          className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] ${
            esProtagonista
              ? 'bg-brand-primary/[0.08] text-brand-primary font-semibold'
              : 'bg-black/[0.05] text-black/65 font-medium'
          }`}
        >
          {esProtagonista && <Star className="w-3 h-3" strokeWidth={2.2} />}
          {rolPorId(rol).nombre}
        </span>
        <ChevronRight
          className={`w-4 h-4 shrink-0 text-black/30 transition-transform ${
            abierto ? 'rotate-90' : ''
          }`}
        />
      </button>
      {abierto && (
        // Menú anclado a la fila. No es un panel lateral: son tres opciones de una
        // palabra, y mandarlas a la capa de 615px sería sacar al usuario de la lista que
        // está justo mirando.
        <div className="absolute z-20 right-0 top-full mt-1 w-[280px] rounded-2xl bg-white p-1.5 shadow-lg ring-1 ring-black/[0.08]">
          {ROLES_PRODUCTO.map((r) => {
            const activo = r.id === rol;
            // El protagonista sólo cambia ascendiendo a otro: ver el comentario del
            // componente.
            const bloqueado = esProtagonista && r.id !== 'protagonista';
            return (
              <button
                key={r.id}
                type="button"
                disabled={bloqueado}
                onClick={() => onElegir(r.id)}
                className={`w-full text-left rounded-xl px-2.5 py-2 transition-colors ${
                  activo
                    ? 'bg-brand-primary/[0.07]'
                    : bloqueado
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-black/[0.04]'
                }`}
              >
                <span
                  className={`block text-[13px] ${
                    activo
                      ? 'font-semibold text-brand-primary'
                      : 'font-medium text-brand-black'
                  }`}
                >
                  {r.nombre}
                </span>
                <span className="block text-[12px] leading-snug text-black/50 mt-0.5">
                  {r.descripcion}
                </span>
              </button>
            );
          })}
          {esProtagonista && (
            <p className="px-2.5 py-1.5 text-[11.5px] leading-snug text-black/45">
              Para sacarlo del frente, ascendé a otro producto: siempre hay un
              protagonista.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default function AmbienteCompletoMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  // Qué pantalla se dibuja. Se decide en JS y no sólo con `md:` porque las dos ramas no
  // son la misma pantalla con otro CSS: montarlas a la vez levantaría DOS grillas de
  // productos, y los bottom sheets de mobile se portalean al `body` —fuera del alcance
  // de cualquier `md:hidden` que les pusiéramos encima—. Es el mismo corte de 768px que
  // usa el shell para elegir sidebar o barra inferior, así que la pantalla del modo y su
  // chrome cambian juntos.
  const esEscritorio = useShellEscritorio();
  // La marca activa sale del MISMO hook que el gate de rubro, y no de leer
  // `current_brand_id` una vez al montar: así sigue al cambio de marca con la pantalla
  // abierta (`atlas:brand-changed`) y, en las cuentas que nunca eligieron una, resuelve la
  // `is_default` en vez de `null` —que traía el catálogo de toda la empresa—
  // (ADR-2026-09-30-los-modos-de-hogar-siguen-a-la-marca-activa).
  const { rubro, marcaId: brandId, loaded: rubroCargado } = useRubroMarcaActiva();

  // ── Gate de rubro ─────────────────────────────────────────────────────────
  // La card ya no se muestra fuera de Hogar y Decoración (`soloHogarDeco`), pero la ruta
  // existe igual: alguien con el link, o una cuenta que cambió de marca con la pantalla
  // abierta. Con el rubro resuelto y sin ser el correcto, a la home.
  useEffect(() => {
    if (rubroCargado && !esRubroHogarDeco(rubro)) {
      navigate('/interfaz-v2', { replace: true });
    }
  }, [rubroCargado, rubro, navigate]);

  // ── Cuenta y productos ────────────────────────────────────────────────────
  // El contexto de productos lo provee `InterfazV2` y esta pantalla vive afuera, así
  // que los trae por su cuenta y los provee ella —lo mismo que hace `OmniRefAttach`—.
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [productosCatalogo, setProductosCatalogo] = useState<MockProductV2[]>([]);
  const [productosCargados, setProductosCargados] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      // Sólo la empresa: la marca la resuelve `useRubroMarcaActiva`, arriba.
      const { data: perfil } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      setCompanyId((perfil as { id_company?: string | null } | null)?.id_company ?? null);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // Se espera a que la marca resuelva (`rubroCargado`): con `brandId` todavía en `null`,
  // `fetchUserProducts` trae el catálogo de TODA la empresa, y una marca de otro rubro
  // asomaría en la grilla hasta que llegara la respuesta buena.
  const cargarProductos = useCallback(async () => {
    if (!companyId || !rubroCargado) return;
    try {
      const lista = await fetchUserProducts(companyId, brandId);
      setProductosCatalogo(lista);
    } catch (e) {
      console.error('[AmbienteCompletoMode] productos', e);
    } finally {
      setProductosCargados(true);
    }
  }, [companyId, brandId, rubroCargado]);

  useEffect(() => {
    if (!companyId || !rubroCargado) return;
    let cancelado = false;
    (async () => {
      try {
        const lista = await fetchUserProducts(companyId, brandId);
        if (!cancelado) setProductosCatalogo(lista);
      } catch (e) {
        console.error('[AmbienteCompletoMode] productos', e);
      } finally {
        if (!cancelado) setProductosCargados(true);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId, rubroCargado]);

  const recargarFondos = useCallback(async () => {
    if (!companyId) return;
    try {
      setFondosGuardados(await fetchFondosGuardados(companyId));
    } catch (e) {
      console.error('[AmbienteCompletoMode] fondos guardados', e);
    }
  }, [companyId]);

  useEffect(() => {
    void recargarFondos();
  }, [recargarFondos]);

  /**
   * Las miniaturas de la card de productos VACÍA: los últimos productos que subió el
   * usuario, no dos ilustraciones fijas
   * (ADR-2026-09-10-la-card-vacia-muestra-los-ultimos-productos).
   *
   * `fetchUserProducts` devuelve el catálogo ordenado por `created_at` descendente, así
   * que los primeros de la lista SON los últimos cargados. Se filtran los que no tienen
   * imagen firmada —un hueco blanco en el abanico se lee como una foto rota— y, si no
   * queda ninguno (cuenta nueva, o el catálogo todavía cargando), caen las ilustraciones
   * de siempre.
   */
  const miniaturasVacio = useMemo(() => {
    const conFoto = productosCatalogo
      .map((p) => p.imageUrl)
      .filter((u): u is string => !!u)
      .slice(0, MINIATURAS_CARD_VACIA);
    return conFoto.length > 0 ? conFoto : PLACEHOLDERS_MUEBLES;
  }, [productosCatalogo]);

  const productEditContextValue = useMemo(
    () => ({ companyId, refreshProducts: cargarProductos }),
    [companyId, cargarProductos],
  );

  // ── Lo que se elige ───────────────────────────────────────────────────────
  // Arranca en la presentación SÓLO en el teléfono: en desktop ese paso no existe (ver
  // `Paso`). `useShellEscritorio` resuelve sincrónicamente en el primer render, así que
  // acá ya se sabe cuál es; y cruzar los 768px remonta el árbol entero, que es cuando
  // este valor se vuelve a elegir.
  const [paso, setPaso] = useState<Paso>(esEscritorio ? 'producto' : 'presentacion');
  const [productos, setProductos] = useState<MockProductV2[]>([]);
  const [roles, setRoles] = useState<Record<string, RolProducto>>({});
  const [ambienteId, setAmbienteId] = useState<AmbienteDeEscena>('living');
  // El ambiente escrito a mano se guarda APARTE del id: probar un curado y arrepentirse
  // no borra lo que se había escrito (ADR-2026-09-04-donde-pasa-tiene-una-opcion-para-describirlo).
  const [ambientePropio, setAmbientePropio] = useState('');
  // Ídem la FOTO propia del lugar: el fondo elegido queda al lado del id, y pasar a un
  // curado no lo pierde (ADR-2026-09-28-ambiente-completo-fondo-propio).
  const [fondoElegido, setFondoElegido] = useState<FondoGuardado | null>(null);
  /**
   * "Mis fondos": los que la empresa ya subió —desde acá o desde Modelo—, para volver a
   * elegirlos sin subirlos de nuevo. Es la misma tabla `fondos_personalizados`.
   */
  const [fondosGuardados, setFondosGuardados] = useState<FondoGuardado[]>([]);
  /**
   * La vista del panel de Ambiente: la grilla, o la subida de un fondo propio adentro
   * del mismo panel, como en Modelo (ADR-2026-08-25-el-fondo-se-sube-en-el-panel).
   */
  const [vistaAmbiente, setVistaAmbiente] = useState<'grilla' | 'subir'>('grilla');
  const [cantidad, setCantidad] = useState(ESCENAS_DEFAULT);
  const [calidad, setCalidad] = useState<CalidadImagen>(CALIDAD_DEFAULT);
  const [formatoId, setFormatoId] = useState<string>(FORMATO_DEFAULT);
  const [instruccion, setInstruccion] = useState('');
  const [tandas, setTandas] = useState<TandaEscena[]>([]);
  /** Lo que salió mal en el último pedido, ya mostrable; se limpia al volver a generar. */
  const [error, setError] = useState<string | null>(null);
  const [limitModal, setLimitModal] = useState<'free-trial-ended' | 'plan-limit-reached' | null>(null);
  const [panelLateral, setPanelLateral] = useState<PanelLateral>(null);
  const [rolAbierto, setRolAbierto] = useState<string | null>(null);
  const [subirProductoAbierto, setSubirProductoAbierto] = useState(false);

  // Cerrar el panel de Ambiente vuelve a la grilla: reabrirlo tiene que mostrar la
  // decisión entera, no la subida a medio camino.
  useEffect(() => {
    if (panelLateral !== 'ambiente') setVistaAmbiente('grilla');
  }, [panelLateral]);
  /**
   * Lo que está abierto AHORA, para leerlo cuando una subida termina: la subida es una
   * promesa que sigue aunque el panel se cierre, y su `onSubido` capturó el render de cuando
   * arrancó. Si mientras tanto la persona cerró el panel y eligió un curado, el fondo que
   * llega tarde NO puede pisar esa elección (review de Greptile, PR #150): queda guardado en
   * "Mis fondos" y nada más.
   */
  const panelLateralRef = useRef(panelLateral);
  panelLateralRef.current = panelLateral;
  const vistaAmbienteRef = useRef(vistaAmbiente);
  vistaAmbienteRef.current = vistaAmbiente;


  // Los roles siguen a la selección: el que entra arranca en "apoyo", el que sale se va, y
  // siempre queda exactamente un protagonista (`rolesDeSeleccion`). Se recalcula sobre los
  // ids y no sobre el array de productos porque el picker devuelve objetos nuevos en cada
  // emisión.
  const idsProductos = productos.map((p) => p.id).join(',');
  useEffect(() => {
    setRoles((previos) =>
      rolesDeSeleccion(
        idsProductos ? idsProductos.split(',') : [],
        previos,
      ),
    );
  }, [idsProductos]);

  // Cambiar de marca con la pantalla abierta vacía la selección: los productos son de la
  // marca anterior, y el pedido saldría con la marca nueva y los muebles de la otra
  // (review de Greptile, PR #153). Sólo entre dos marcas YA resueltas: la primera vez
  // que `brandId` deja de ser `null` es la carga, no un cambio. Los roles se van solos
  // con el efecto de arriba.
  const marcaPreviaRef = useRef(brandId);
  useEffect(() => {
    const previa = marcaPreviaRef.current;
    marcaPreviaRef.current = brandId;
    if (previa && brandId && previa !== brandId) setProductos([]);
  }, [brandId]);

  // El menú de roles se cierra al tocar afuera, como cualquier popover.
  useEffect(() => {
    if (!rolAbierto) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-menu-rol]')) return;
      setRolAbierto(null);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [rolAbierto]);

  // ── Panel lateral: dónde arranca ──────────────────────────────────────────
  // Se mide el borde derecho de la columna de configuración, como en Modelo y Catálogo
  // (ADR-2026-09-02-el-panel-lateral-arranca-donde-termina-la-configuracion): el ancho
  // es elástico y escribirlo acá lo dejaría tapando la columna en cuanto cambie.
  const areaRef = useRef<HTMLDivElement>(null);
  const panelConfigRef = useRef<HTMLElement>(null);
  /**
   * El overlay de "Generando…" a pantalla completa de mobile — el mismo `LoadingAnimation`
   * que usan Automático y el resto de la app cuando no hay una columna al costado donde
   * mostrar la tanda. Al terminar, la pantalla pasa a `resultados`.
   */
  const [generandoMobile, setGenerandoMobile] = useState<boolean>(false);
  /** Corta la barra del overlay cuando la respuesta llega (antes del `durationMs`). */
  const [completeNow, setCompleteNow] = useState(false);

  // ── La columna de resultados (desktop) ────────────────────────────────────
  // La misma de Animar imagen, Carrusel y Belleza: recibe la promesa del pedido ya en
  // vuelo, dibuja los recuadros, precarga las imágenes y muestra los errores. En mobile
  // no hay columna: la tanda va a la pantalla de resultados del modo.
  const { columna, hayColumna, visorAbierto, lanzarTanda, puedeCrear } =
    useColumnaResultadosModo({
      companyId,
      brandId,
      modo: PATH_MODO,
      onError: setError,
      onLimitePlan: () => setLimitModal('plan-limit-reached'),
    });
  const [offsetPanelLateral, setOffsetPanelLateral] = useState<number | string>(
    `calc(${ANCHO_PANEL_CONFIG} + ${SEPARACION_PANEL}px)`,
  );
  useLayoutEffect(() => {
    const aside = panelConfigRef.current;
    const area = areaRef.current;
    if (!aside || !area) return;
    const medir = () => {
      const a = aside.getBoundingClientRect();
      const r = area.getBoundingClientRect();
      setOffsetPanelLateral(Math.round(a.right - r.left) + SEPARACION_PANEL);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(aside);
    ro.observe(area);
    return () => ro.disconnect();
    // `paso` en las dependencias: cada paso dibuja su propio `<aside>`, y el observer
    // tiene que engancharse al que está montado.
  }, [paso]);

  const alternarPanel = (p: Exclude<PanelLateral, null>) =>
    setPanelLateral((actual) => (actual === p ? null : p));

  // ── Mobile: el back del chrome ────────────────────────────────────────────
  // El paso 2 NO dibuja su propio "Volver": el chrome flotante del shell ya pone uno
  // arriba a la izquierda, y las dos flechas hacían el mismo movimiento una encima de la
  // otra (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2). Así que el back de
  // arriba retrocede DE PASO, y sólo desde el primero se va del modo. Además, ponerlo
  // esconde la barra inferior de navegación (`MobileBottomNav` se apaga con un override
  // puesto), que es lo que hacen el resto de los modos al ocupar la pantalla entera.
  //
  // Desde "Armar la escena" el back vuelve a la PRESENTACIÓN y no a la grilla: la grilla
  // es un paso de ida —se entra, se elige y se sale con "Siguiente"—, y volver ahí
  // devolvía a una pantalla de selección que ya estaba resuelta. Para cambiar los
  // productos está la card de la escena, que abre esa misma grilla a propósito.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      // De los resultados se vuelve a la escena —con los ajustes tal como quedaron—, que
      // es de donde salieron.
      if (paso === 'resultados') setPaso('configurar');
      else if (paso === 'configurar' || paso === 'producto') setPaso('presentacion');
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
  }, [paso, setMobileBackOverride, navigate]);

  // ── La tanda ──────────────────────────────────────────────────────────────
  const costo = costoEscenas(cantidad, calidad);
  /** El ambiente lo escribió el usuario en vez de elegir uno de los cinco curados. */
  const esAmbientePropio = ambienteId === AMBIENTE_PROPIO_ID;
  /** El ambiente es una FOTO propia del lugar (subida acá o elegida de "Mis fondos"). */
  const esFondoPropio = ambienteId === AMBIENTE_FONDO_ID;
  const ambiente = ambientePorId(ambienteId as AmbienteId);
  const IconoAmbiente = esAmbientePropio
    ? PencilLine
    : esFondoPropio
      ? ImageIcon
      : ICONO_AMBIENTE[ambiente.id];
  /** Cómo se nombra lo elegido: el curado, lo escrito, o el nombre del fondo. */
  const ambienteEtiqueta = esAmbientePropio
    ? ambientePropio.trim()
    : esFondoPropio
      ? fondoElegido?.nombre ?? ''
      : ambiente.nombre;

  const falta: string | null =
    productos.length < MIN_PRODUCTOS
      ? `Elegí al menos ${MIN_PRODUCTOS} productos para armar la escena.`
      : // Un ambiente propio sin escribir, o un fondo propio sin foto, es una escena
        // sin lugar: el Generar espera.
        faltaDelAmbiente(ambienteId, ambientePropio, fondoElegido);

  const generar = () => {
    if (falta) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    setError(null);
    // El tope de tandas en curso se pregunta ANTES de disparar: acá todavía se puede
    // frenar y decirlo (ADR-2026-09-06-tope-de-tres-tandas-en-curso). Sólo en desktop:
    // en mobile la tanda no ocupa un lugar en la columna.
    if (esEscritorio && !puedeCrear()) {
      setError(
        'Ya hay varias generaciones en curso. Esperá a que termine alguna para pedir otra.',
      );
      return;
    }
    /**
     * El `execution_id`, apenas el backend acepta. Queda anotado en la tanda del store:
     * sin eso, recargar pierde el seguimiento de una corrida que el backend ya aceptó y
     * que va a cobrar igual (ADR-2026-09-25-revision-greptile-la-tanda-aceptada-se-puede-reanudar).
     * Mismo diferido que Variantes de acabado. Si el pedido no llega a aceptarse, cierra
     * en `null` y no hay nada que anotar.
     */
    let resolverExecution: (id: string | null) => void = () => {};
    const executionDeLaTanda = new Promise<string | null>((res) => {
      resolverExecution = res;
    });

    // El payload del contrato (`docs/2026-09-27-ambiente-completo-contrato-front.md` del
    // backend): el rol de cada producto —lo propio del modo—, el ambiente como id cuando
    // es curado, como texto cuando lo escribió el usuario y como `{ tipo: 'fondo',
    // file_name }` cuando es una foto propia del lugar; el formato como RATIO y la
    // calidad como `image_size`. Los créditos NO viajan: los calcula el backend.
    const pedido = pedirAmbienteCompleto(
      {
        productos: productos.map((p) => ({
          id: p.id,
          nombre: p.name,
          rol: roles[p.id] ?? 'apoyo',
        })),
        ambiente: ambienteDelPedido(ambienteId, ambientePropio, fondoElegido),
        cantidad,
        formato: formatSlugToAspect(formatoId),
        image_size: calidad,
        instruccion: instruccion.trim() || null,
        ...(brandId ? { brand_id: brandId } : {}),
      },
      { onAceptado: resolverExecution },
    );
    pedido.catch(() => resolverExecution(null));
    const ratio = ratioDeFormato(formatoId);

    /**
     * La tanda va al store SIEMPRE, también en el teléfono
     * (ADR-2026-09-25-revision-greptile-en-el-telefono-la-tanda-tambien-se-anota): es lo
     * único que anota el `execution_id`, y sin él recargar el teléfono a mitad de una
     * generación perdía el rastro de algo que el backend iba a cobrar igual. De paso, la
     * generación aparece en el aviso de "generando de fondo" y, si la persona gira a
     * desktop, en su columna. En desktop además es lo que se ve: la promesa se entrega SIN
     * esperarla, los recuadros salen ya y el usuario se queda en el modo.
     *
     * Devuelve `false` con el tope de tandas en curso lleno: ahí el hook avisa y no crea
     * tanda; en desktop ya se frenó arriba, en mobile la tanda local sigue igual.
     */
    lanzarTanda({
      total: cantidad,
      ratio,
      creditos: costo,
      duracionEstimadaMs: DURACION_GENERACION_MS,
      resultado: pedido,
      executionId: executionDeLaTanda,
      errorGenerico: 'No se pudieron generar las escenas. Intentá nuevamente.',
    });

    if (esEscritorio) {
      // La columna compartida dibuja SÓLO lo que salió y cierra la tanda con eso: una
      // entrega parcial no se distingue de una completa. Se dice debajo del Generar (en
      // mobile lo dice la tanda misma, al pie). Las que faltan no se cobraron.
      pedido
        .then((ids) => {
          if (ids.length < cantidad) setError(avisoDeEntregaParcial(ids.length, cantidad));
        })
        .catch(() => {
          /* el error lo reporta la columna */
        });
      return;
    }

    // MOBILE: la generación se lleva la pantalla. No hay costado donde dibujar la tanda,
    // así que va el overlay de toda la app y, al terminar, la pantalla de resultados con
    // las escenas que salieron.
    const id = `${Date.now()}`;
    setTandas((t) => [
      {
        id,
        cantidad,
        ratio,
        estado: 'generando',
        imagenes: [],
        ambienteEtiqueta,
        productos: productos.length,
        creadaEn: Date.now(),
      },
      ...t,
    ]);
    setCompleteNow(false);
    setGenerandoMobile(true);
    pedido
      .then(async (ids) => {
        let imagenes: PreloadedImage[] = [];
        try {
          imagenes = await preloadGeneratedImages(ids, companyId);
        } catch (e) {
          console.warn('[AmbienteCompletoMode] preload falló', e);
        }
        // Las escenas EXISTEN (el backend las entregó y cobró) pero no se pudieron firmar o
        // cargar: una tanda "lista" sin imágenes sería un encabezado vacío sin explicación.
        // Se dice dónde están (review de Greptile, PR #151).
        setTandas((t) =>
          t.map((x) =>
            x.id !== id
              ? x
              : imagenes.length > 0
                ? { ...x, estado: 'lista', imagenes, entregadas: ids.length }
                : {
                    ...x,
                    estado: 'fallida',
                    error:
                      'Las escenas se generaron pero no pudimos cargarlas acá. Las encontrás en Creaciones.',
                  },
          ),
        );
        setCompleteNow(true);
      })
      .catch((err: unknown) => {
        console.error('[AmbienteCompletoMode] la generación falló', err);
        if (err instanceof GenerateAdError && err.errorType === 'billing') {
          // Sin créditos no hay tanda que mostrar: se saca, se cierra el overlay y se
          // abre el modal del plan sobre la configuración.
          setTandas((t) => t.filter((x) => x.id !== id));
          setGenerandoMobile(false);
          setLimitModal('plan-limit-reached');
          return;
        }
        const mensaje =
          err instanceof GenerateAdError && err.isActionable
            ? err.message
            : 'No se pudieron generar las escenas. Intentá nuevamente.';
        setTandas((t) =>
          t.map((x) => (x.id === id ? { ...x, estado: 'fallida', error: mensaje } : x)),
        );
        setCompleteNow(true);
      });
  };

  // ── Migas ─────────────────────────────────────────────────────────────────
  // Igual que el Catálogo: el modo en el medio, el paso al final. Volver de "Armar la
  // escena" a los productos es tocar el nombre del modo.
  const nombreModo = nombreCortoModo(PATH_MODO, 'Ambiente completo');
  const migas =
    paso === 'configurar'
      ? [
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreModo, onClick: () => setPaso('producto') },
          { label: 'Armar la escena' },
        ]
      : [
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreModo },
        ];

  // ── Piezas ────────────────────────────────────────────────────────────────

  /**
   * "Quién manda en la foto" — una fila por producto, pegada a la card de productos.
   *
   * Es lo que hace que este modo no sea "el Catálogo con varios productos": la IA tiene
   * que saber quién va adelante. Con un solo producto elegido no se dibuja —no hay
   * jerarquía que decidir— y con dos o más es lo primero que se ve debajo de la card.
   */
  const bloqueJerarquia = productos.length > 1 && (
    <div data-menu-rol className="rounded-2xl bg-black/[0.035] p-3 flex flex-col gap-0.5">
      <p className="px-2 mb-1.5 text-[11px] font-bold uppercase tracking-wide text-black/45 leading-none">
        Quién manda en la foto
      </p>
      {productos.map((p) => (
        <FilaRol
          key={p.id}
          producto={p}
          rol={roles[p.id] ?? 'apoyo'}
          abierto={rolAbierto === p.id}
          onToggle={() => setRolAbierto((a) => (a === p.id ? null : p.id))}
          onElegir={(r) => {
            setRoles((previos) =>
              r === 'protagonista'
                ? conProtagonista(previos, p.id)
                : { ...previos, [p.id]: r },
            );
            setRolAbierto(null);
          }}
        />
      ))}
    </div>
  );

  const cardAmbiente = (
    <CardFila
      etiqueta="Ambiente"
      valor={
        esAmbientePropio
          ? ambientePropio.trim() || 'Escribí cómo es el ambiente'
          : esFondoPropio
            ? fondoElegido
              ? `Tu fondo · ${fondoElegido.nombre}`
              : VALOR_FONDO_SIN_FOTO
            : `${ambiente.nombre} · ${ambiente.descripcion}`
      }
      activa={panelLateral === 'ambiente'}
      onClick={() => alternarPanel('ambiente')}
      miniatura={
        // La miniatura es la FOTO de lo elegido: el curado o el fondo propio. Sin foto
        // (descrito a mano, o fondo propio todavía sin subir), el ícono de la opción.
        esFondoPropio && fondoElegido?.imageUrl ? (
          <CachedImg
            src={fondoElegido.imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : !esAmbientePropio && !esFondoPropio && ambiente.imageUrl ? (
          <CachedImg
            src={ambiente.imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <IconoAmbiente className="w-6 h-6 text-black/45" />
        )
      }
    />
  );

  /**
   * La instrucción libre, ÚLTIMA y a todo el alto que sobre — el mismo criterio que
   * "Mostrar cómo funciona" (ADR-2026-09-03-el-paso-2-cierra-con-la-instruccion): primero
   * se decide y lo último es afinar con palabras, y un campo de texto más alto se usa
   * mientras que un hueco no.
   *
   * En las dos superficies se lleva el alto que sobra —también en el teléfono, donde si
   * no queda una franja blanca de media pantalla entre el campo y la barra del pie—, con
   * un piso más bajo en mobile para que con la escena larga siga entrando algo de la
   * lista arriba. El 16px de mobile no es un capricho de escala: abajo de eso, iOS hace
   * auto-zoom al enfocar (ADR-2026-08-03-inputs-16px-en-mobile-sin-auto-zoom).
   */
  const campoInstruccion = (variante: 'columna' | 'mobile') => (
    <div
      className={`relative flex-1 flex flex-col ${
        variante === 'columna' ? 'min-h-[112px]' : 'min-h-[104px]'
      }`}
    >
      <textarea
        value={instruccion}
        onChange={(e) => setInstruccion(e.target.value)}
        placeholder="Agregá indicaciones extras para afinar el resultado"
        className={`flex-1 w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-brand-black placeholder:text-black/35 resize-none transition-colors ${
          variante === 'columna' ? 'text-[14.5px]' : 'text-[16px]'
        }`}
      />
      {/* El `companyId` va explícito: esta pantalla vive afuera del `AgentEnvContext`
          de `InterfazV2`, de donde el botón lo saca por defecto. */}
      <PromptRecientesButton
        tipo="instruccion"
        companyId={companyId}
        onPick={setInstruccion}
        titulo="Instrucciones usadas antes"
        className="absolute bottom-2.5 right-2.5"
      />
    </div>
  );

  /** Lo que salió mal en el último pedido, debajo del Generar (mismo dibujo que Belleza). */
  const errorBox = error ? (
    <div className="rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  // El pie fijo del paso 2: los ajustes de siempre —Calidad y Formato, con las celdas de
  // `AjustesRapidosProducto` (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos)—
  // y el Generar con el contador pegado, que es lo que multiplica el costo.
  const pie =
    paso === 'producto' ? (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setPaso('configurar')}
          disabled={productos.length < MIN_PRODUCTOS}
          className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
        >
          Siguiente
        </button>
        {productos.length < MIN_PRODUCTOS && (
          <p className="text-[13px] text-black/50 text-center leading-snug">
            Elegí de {MIN_PRODUCTOS} a {MAX_PRODUCTOS} productos: van a entrar juntos en
            la misma foto.
          </p>
        )}
      </div>
    ) : (
      <>
        <div className="grid grid-cols-2 gap-2">
          <CeldaAjuste etiqueta="Calidad">
            <CalidadSegmentada calidad={calidad} onChange={setCalidad} />
          </CeldaAjuste>
          <CeldaAjuste
            etiqueta="Formato"
            onClick={() => alternarPanel('formato')}
            abrePanelLateral
          >
            <ValorConChevron texto={getFormatLabel(formatoId)} />
          </CeldaAjuste>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-stretch gap-2">
            {/* La cantidad, PEGADA al Generar: es el único ajuste que cambia lo que dice
                el botón y lo que cuesta la tanda (mismo criterio que Modelo). El
                `[&>span]:mt-0` neutraliza el `mt-1` que `ContadorImagenes` trae de
                cuando vivía debajo de una etiqueta. */}
            <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 h-12 flex items-center [&>span]:mt-0">
              <ContadorImagenes
                valor={cantidad}
                max={MAX_ESCENAS}
                onChange={setCantidad}
                disabled={!!falta}
              />
            </div>
            <button
              type="button"
              onClick={generar}
              disabled={!!falta}
              className="flex-1 min-w-0 h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
            >
              Generar {cantidad} {cantidad === 1 ? 'escena' : 'escenas'}
              <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {costo}
              </span>
            </button>
          </div>
          {/* El estado DEBAJO del botón, que no se mueve (ADR-2026-08-31 en Modelo). */}
          {falta && <p className="text-[13px] text-black/50 px-1">{falta}</p>}
          {errorBox}
        </div>
      </>
    );

  // La cabecera de la pantalla —migas + título— ADENTRO de la columna, en su borde de
  // arriba, como en Automático y "Mostrar cómo funciona": así la fila de columnas arranca
  // pegada al techo y la columna se lee entera como la pantalla del modo.
  const cabecera = (
    <div className="px-6 pt-5">
      <EncabezadoPantalla migas={migas} className="!px-0 !pt-0 !pb-2" />
      <h1
        key={paso}
        className="animate-paso-titulo text-[21px] font-semibold leading-tight text-brand-black"
      >
        {nombreModo}
      </h1>
    </div>
  );

  const claseColumna =
    'grow-0 shrink-0 flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden';

  // ── Paso 1: la grilla de productos ADENTRO de la columna ──────────────────
  // Como Automático (ADR-2026-08-21-automatico-con-el-diseno-nuevo) y "Mostrar cómo
  // funciona": buscador, orden, "Subir nuevo producto" como primera tarjeta y los
  // productos a tres columnas, todo a la vista sin abrir nada. Acá la selección es
  // MÚLTIPLE con tope (`maxSeleccion`) y el primero elegido queda marcado "Principal":
  // es el que arranca de protagonista en el paso 2.
  const columnaProducto = (
    <aside
      ref={panelConfigRef}
      style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
      className={claseColumna}
    >
      <PickProductPanelModelo
        key="paso-producto"
        variant="desktop"
        hideBack
        rubro={rubro}
        presetKind="ambiente-completo"
        maxSeleccion={MAX_PRODUCTOS}
        // "Principal" sobre la primera elegida: acá es verdad, porque el orden de
        // selección es lo que decide el protagonista de arranque (`rolesDeSeleccion`).
        showPrimary
        initialSelectedIds={productos.map((p) => p.id)}
        title="Elegí tus productos"
        description={`De ${MIN_PRODUCTOS} a ${MAX_PRODUCTOS} muebles que van a compartir la misma foto. El primero que elijas arranca de protagonista.`}
        cabecera={cabecera}
        onSubirProducto={() => setSubirProductoAbierto(true)}
        nombreSobreLaFoto
        columnasFijas={3}
        pistasDeUso
        onSelectionChange={setProductos}
        onConfirm={() => setPaso('configurar')}
        onBack={() => navigate('/interfaz-v2')}
        footerSiempreVisible
        confirmLabel="Siguiente"
        onConfirmOverride={() => setPaso('configurar')}
        confirmDisabled={productos.length < MIN_PRODUCTOS}
        confirmHint={
          productos.length < MIN_PRODUCTOS
            ? `Elegí de ${MIN_PRODUCTOS} a ${MAX_PRODUCTOS} productos: van a entrar juntos en la misma foto.`
            : undefined
        }
      />
    </aside>
  );

  // ── Paso 2: la configuración de la escena ─────────────────────────────────
  const columnaConfiguracion = (
    <aside
      ref={panelConfigRef}
      style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
      className={claseColumna}
    >
      {cabecera}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
        {/* `overflow-visible` NO: el scroller tiene que recortar. El menú de roles se
            abre hacia abajo desde una fila que está arriba de todo, así que entra. */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pb-4">
          <CardProductos
            products={productos}
            copyVacio="Elegí tus productos"
            copyElegido="Editar la escena"
            placeholders={miniaturasVacio}
            onAbrirProductos={() => alternarPanel('productos')}
            // Soltar un archivo encima abre el panel: subir producto desde acá es la
            // siguiente vuelta (ver pendientes en el doc del proceso).
            onSubirArchivos={() => alternarPanel('productos')}
          />
          {bloqueJerarquia}
          {cardAmbiente}
          {campoInstruccion('columna')}
        </div>
        <div className="shrink-0 flex flex-col gap-3 pt-3 bg-white border-t border-black/[0.06]">
          {pie}
        </div>
      </div>
    </aside>
  );

  const columnaDerecha = (
    <div className={`flex-1 min-w-0 flex flex-col overflow-hidden ${visorAbierto ? '' : 'relative'}`}>
      {!hayColumna ? (
        // La MUESTRA del modo hasta que haya algo generado
        // (ADR-2026-08-21-modelo-v2-la-muestra-se-queda), como un bloque —título + foto—
        // centrado (ADR-2026-09-03-la-muestra-del-modo-es-un-bloque).
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6">
          <div className="shrink-0 text-center max-w-[440px] mb-4">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              {MUESTRA_TITULO}
            </h2>
            <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
              {MUESTRA_BAJADA}
            </p>
          </div>
          <img
            src={PORTADA}
            alt="Ejemplo del modo Ambiente completo"
            className="block min-h-0 w-auto max-w-[420px] object-contain rounded-2xl"
          />
        </div>
      ) : (
        // Las tandas de la sesión, en la columna compartida de resultados: recuadros
        // mientras generan, las escenas al terminar, el visor al tocar una.
        columna
      )}
    </div>
  );

  const panelProductos = (
    // Ver el comentario de `PresetDetailSheetModelo` sobre este wrapper: el panel de
    // productos arma su raíz `h-full` sin `min-h-0`, y metido en un flex column desborda
    // en vez de comprimirse; el `[&>div]:` lo vuelve un flex item que se estira.
    <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
      <PickProductPanelModelo
        variant="desktop"
        hideBack
        hideFooter
        rubro={rubro}
        maxSeleccion={MAX_PRODUCTOS}
        // SIN `showPrimary`, al revés que el paso 1: acá los roles ya se pueden haber
        // cambiado a mano, así que "Principal" sobre la primera elegida diría algo que el
        // bloque de al lado desmiente. La respuesta a "quién va adelante" vive en
        // "Quién manda en la foto", que está a la vista mientras el panel está abierto.
        initialSelectedIds={productos.map((p) => p.id)}
        title="Elegí los productos"
        description={`De ${MIN_PRODUCTOS} a ${MAX_PRODUCTOS} muebles que van a compartir la misma foto.`}
        // Selección EN VIVO, como en Modelo: cada tap ya deja el producto en la columna.
        // El panel NO se cierra solo —a diferencia del de "Mostrar cómo funciona", que
        // elige uno— porque acá elegir es una tanda de taps: cerrarlo en el primero
        // obligaría a reabrirlo por cada mueble.
        onSelectionChange={setProductos}
        onConfirm={() => setPanelLateral(null)}
        onBack={() => setPanelLateral(null)}
      />
    </div>
  );

  /**
   * La decisión del ambiente, entera: la bajada, la grilla —los cinco curados más la
   * tarjeta para escribirlo— y, con esa última elegida, el campo donde se escribe.
   *
   * Es una función y no un nodo porque la dibujan DOS superficies con distinta densidad:
   * el panel lateral de desktop (tres columnas) y el bottom sheet de mobile (dos). El
   * contenido es el mismo en las dos: escribir el ambiente tiene que poder hacerse
   * también desde el teléfono.
   */
  const bloqueAmbientes = (columnas: number) =>
    vistaAmbiente === 'subir' ? (
      // La subida de un fondo propio, ADENTRO del panel y con la misma vista que Modelo:
      // el fondo nuevo queda elegido y se vuelve a la grilla, donde aparece marcado en
      // "Mis fondos" (ADR-2026-09-28-ambiente-completo-fondo-propio).
      <SubirFondoView
        companyId={companyId}
        onVolver={() => setVistaAmbiente('grilla')}
        onSubido={(saved) => {
          void recargarFondos();
          // La subida terminó con el panel cerrado o en otra vista: la elección vigente es
          // la que hizo la persona después, no ésta. El fondo ya está en "Mis fondos".
          if (panelLateralRef.current !== 'ambiente' || vistaAmbienteRef.current !== 'subir') {
            return;
          }
          setFondoElegido(saved);
          setAmbienteId(AMBIENTE_FONDO_ID);
          setVistaAmbiente('grilla');
        }}
      />
    ) : (
      <>
        <p className="text-[12.5px] text-[#3C3C43]/60 mb-4">{BAJADA_AMBIENTE}</p>
        <GrillaAmbientes
          ambienteId={ambienteId}
          columnas={columnas}
          onElegir={(id) => {
            setAmbienteId(id);
            setPanelLateral(null);
          }}
          onElegirPropio={() => setAmbienteId(AMBIENTE_PROPIO_ID)}
          onSubirFondo={() => setVistaAmbiente('subir')}
        />
        {/* El campo aparece DEBAJO de la grilla y sólo con esa opción elegida: el panel es
            la decisión entera, así que escribir tiene que pasar acá adentro y no en otra
            pantalla. Es el mismo textarea que la instrucción de la columna, con su historial
            de instrucciones usadas antes. */}
        {esAmbientePropio && (
          <div className="mt-4">
            <label
              htmlFor="ambiente-propio"
              className="block text-[12.5px] font-semibold text-brand-black mb-1.5"
            >
              Contá cómo es el ambiente
            </label>
            <div className="relative">
              <textarea
                id="ambiente-propio"
                autoFocus
                value={ambientePropio}
                onChange={(e) => setAmbientePropio(e.target.value)}
                placeholder="Un local de diseño con piso de cemento y ventanal al norte, luz de media tarde"
                // 16px hasta `md:`, como el resto de los campos del modo en el teléfono:
                // abajo de eso iOS hace auto-zoom al enfocar
                // (ADR-2026-08-03-inputs-16px-en-mobile-sin-auto-zoom).
                className="w-full min-h-[104px] rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[16px] md:text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
              />
              <PromptRecientesButton
                tipo="instruccion"
                companyId={companyId}
                onPick={setAmbientePropio}
                titulo="Instrucciones usadas antes"
                className="absolute bottom-2.5 right-2.5"
              />
            </div>
            <p className="mt-2 text-[12px] leading-snug text-black/45">
              Los muebles salen de tus fotos: acá va lo de alrededor —el lugar, los objetos y
              la luz—.
            </p>
          </div>
        )}
        {/* "Mis fondos": lo que la empresa ya subió, acá o desde Modelo, para elegirlo de un
            toque. Sólo si tiene alguno —una sección vacía no es una opción, es un callejón—
            y debajo de la grilla, con su título: son otra cosa que las siete opciones
            (material propio para recorrer), y en la misma corrida el primer fondo se leía
            como una octava. Elegir uno CIERRA el panel, como elegir un curado: es una
            decisión de un tap. */}
        {fondosGuardados.length > 0 && (
          <div className="mt-5 flex flex-col gap-2.5">
            <p className="text-[13px] font-bold text-brand-black/90">Mis fondos</p>
            <div
              className="grid content-start gap-3"
              style={{
                gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))`,
                // La card mide lo que las de la grilla de arriba: 4:3 de ancho de columna
                // más el pie, y no los 150px fijos del panel de Modelo.
                ['--alto-fila' as string]: '120px',
              }}
            >
              {fondosGuardados.map((f) => (
                <CardFondo
                  key={`guardado-${f.id}`}
                  nombre={f.nombre}
                  imageUrl={f.imageUrl}
                  elegido={esFondoPropio && fondoElegido?.fileName === f.fileName}
                  onClick={() => {
                    setFondoElegido(f);
                    setAmbienteId(AMBIENTE_FONDO_ID);
                    setPanelLateral(null);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </>
    );

  const panelAmbiente = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
      {bloqueAmbientes(COLUMNAS_PANEL_LATERAL)}
    </div>
  );

  const panelFormato = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
      <FormatPickerGrid
        selectedId={formatoId}
        onPick={(id) => {
          setFormatoId(id);
          setPanelLateral(null);
        }}
        columnas={COLUMNAS_PANEL_LATERAL}
      />
    </div>
  );

  // ── MOBILE: las dos pantallas ─────────────────────────────────────────────
  // Full-screen debajo del chrome flotante del shell (el back y el menú viven arriba, en
  // 3.5rem de alto más el safe area), con la anatomía de Modelo en el teléfono: la
  // grilla de productos primero, "Armar la escena" después, y en las dos el CTA fijo al
  // pie. Es UN contenedor y no dos portales como en Modelo: acá el modo es la pantalla
  // —no un sheet montado sobre otra—, así que no hay nada debajo que tapar.
  const pantallaMobile = (
    <div
      className="md:hidden fixed inset-0 z-30 bg-white flex flex-col"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {paso === 'presentacion' ? (
        // ── Pantalla 1: la presentación ───────────────────────────────────
        // La misma anatomía que la primera pantalla del modo Automático: la MUESTRA
        // arriba —encabezada por el título y la bajada, como en el Catálogo
        // (ADR-2026-09-02-catalogo-mobile-toma-el-encabezado-de-desktop)— y abajo los dos
        // caminos para traer productos. En desktop este paso no existe: allá la muestra
        // se ve al costado de la grilla, así que una pantalla previa repetiría lo mismo.
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-4 pb-2">
            <div className="shrink-0 text-center max-w-[340px] mb-6">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                {MUESTRA_TITULO}
              </h2>
              <p className="text-[14px] text-[#3C3C43]/60 leading-snug mt-1">
                {MUESTRA_BAJADA}
              </p>
            </div>
            {/* Más chica que el ancho de la pantalla y con tope de alto: con la card
                al pie —que ocupa mucho menos que los dos CTA de antes— la foto se
                comía todo el aire y quedaba pegada al techo y a los bordes. Acotada,
                el bloque título + foto queda centrado en el hueco que sobra. */}
            <img
              src={PORTADA}
              alt="Ejemplo del modo Ambiente completo"
              className="block min-h-0 max-h-[46vh] w-auto max-w-[300px] object-contain rounded-2xl"
            />
          </div>
          {/* La MISMA card de Modelo en mobile (`CardProductos`) en vez de los dos CTA
              de Automático ("Subir nuevo producto" / "Elegir de mis productos") — el
              mismo cambio que ADR-2026-08-23-modelo-v2-mobile: la card muestra lo
              elegido, que los botones no hacían, y "Subir nuevo producto" sigue estando
              como primera tarjeta de la grilla del paso 1, así que la pantalla previa no
              necesita repetirlo. Soltar archivos encima también entra al paso 1, que es
              donde vive el alta (igual que la card del paso 2). */}
          <div className="shrink-0 px-5 pt-4 pb-5 flex flex-col gap-2">
            <CardProductos
              products={productos}
              copyVacio="Elegí tus productos"
              copyElegido="Editar la escena"
              placeholders={miniaturasVacio}
              onAbrirProductos={() => setPaso('producto')}
              onSubirArchivos={() => setPaso('producto')}
            />
            {/* El "Siguiente" aparece con productos ya elegidos: se vuelve acá con el
                back del paso 2, y sin un CTA la única salida hacia adelante era volver a
                entrar a la grilla —una pantalla de selección ya resuelta— para tocar su
                "Siguiente". Con menos del mínimo el botón se ve pero apagado, con la
                misma bajada que el pie del paso 1: explica por qué todavía no se puede. */}
            {productos.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setPaso('configurar')}
                  disabled={productos.length < MIN_PRODUCTOS}
                  className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
                >
                  Siguiente
                </button>
                {productos.length < MIN_PRODUCTOS && (
                  <p className="text-[13px] text-black/50 text-center leading-snug">
                    Elegí de {MIN_PRODUCTOS} a {MAX_PRODUCTOS} productos: van a entrar
                    juntos en la misma foto.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      ) : paso === 'producto' ? (
        // El panel arma su layout sobre un `h-full`, así que sin un contenedor acotado se
        // lleva TODO el alto y su pie queda abajo de la pantalla. Mismo `[&>div]:` que en
        // el panel lateral de desktop.
        <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
          <PickProductPanelModelo
            key="paso-producto-mobile"
            variant="desktop"
            hideBack
            // El título y la bajada van ADENTRO del scroll y se colapsan al bajar: en un
            // teléfono, clavados arriba se comen media pantalla de grilla.
            scrollHeader
            rubro={rubro}
            presetKind="ambiente-completo"
            maxSeleccion={MAX_PRODUCTOS}
            showPrimary
            initialSelectedIds={productos.map((p) => p.id)}
            title="Elegí tus productos"
            description={`De ${MIN_PRODUCTOS} a ${MAX_PRODUCTOS} muebles que van a compartir la misma foto. El primero que elijas arranca de protagonista.`}
            onSubirProducto={() => setSubirProductoAbierto(true)}
            nombreSobreLaFoto
            // SIN `columnasFijas`, al revés que en la columna de desktop: el panel mide su
            // ancho y en un teléfono deja DOS columnas. A tres —lo que hace Modelo, donde
            // la foto es una modelo de cuerpo entero— un mueble queda del tamaño de un
            // ícono y no se distingue un rack de una mesa.
            pistasDeUso
            onSelectionChange={setProductos}
            onConfirm={() => setPaso('configurar')}
            onBack={() => setPaso('presentacion')}
            // El pie es el DEL PANEL, el mismo que en desktop: en este paso el CTA es sólo
            // "Siguiente" y no hay ajustes que mostrar todavía —Calidad, Formato y
            // cantidad recién tienen sentido con la escena armada—.
            footerSiempreVisible
            confirmLabel="Siguiente"
            onConfirmOverride={() => setPaso('configurar')}
            confirmDisabled={productos.length < MIN_PRODUCTOS}
            confirmHint={
              productos.length < MIN_PRODUCTOS
                ? `Elegí de ${MIN_PRODUCTOS} a ${MAX_PRODUCTOS} productos: van a entrar juntos en la misma foto.`
                : undefined
            }
          />
        </div>
      ) : paso === 'resultados' ? (
        // ── Pantalla 3 (SÓLO mobile): las escenas generadas ─────────────────
        // Después del overlay de generación, las escenas se muestran en su PROPIA
        // pantalla y no arriba de los ajustes: lo que se acaba de pedir es lo único que
        // importa mirar, y mezclarlo con los controles que lo pidieron obligaba a
        // scrollear entre las dos cosas. Es el mismo corte que hace Automático en el
        // teléfono, que sale del overlay a la pantalla de resultados.
        <div className="flex-1 min-h-0 flex flex-col page-slide-in-right">
          {/* SIN "Volver" propio, igual que el paso 2: el chrome flotante dibuja el
              suyo y vuelve a "Armar la escena". */}
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Tus escenas
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-6">
            <TandasEscenas tandas={tandas} maxColumnas={2} />
          </div>
          {/* Un solo CTA al pie: volver a la escena, que es donde se cambia algo y se
              vuelve a generar. No repite la barra de ajustes del paso 2 —Calidad,
              Formato y contador con las escenas al lado prometerían un "generar otra
              vuelta" que en realidad es volver—. */}
          <div
            className="shrink-0 px-5 pt-3 pb-4 bg-white border-t border-black/[0.06]"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
          >
            <button
              type="button"
              onClick={() => setPaso('configurar')}
              className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              Volver a la escena
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col page-slide-in-right">
          {/* SIN "Volver" propio: el chrome flotante dibuja el suyo justo encima de esta
              fila y vuelve a la presentación —ver el effect de `mobileBackOverride`—. */}
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Armar la escena
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
            {/* SIN la tanda acá: en mobile las escenas viven en su propia pantalla
                (`resultados`), a la que se llega después del overlay de generación. */}
            <CardProductos
              products={productos}
              copyVacio="Elegí tus productos"
              copyElegido="Editar la escena"
              placeholders={miniaturasVacio}
              // Editar la escena es VOLVER al paso 1: el panel lateral de desktop no
              // existe acá, y la grilla es una pantalla entera.
              onAbrirProductos={() => setPaso('producto')}
              onSubirArchivos={() => setPaso('producto')}
            />
            {bloqueJerarquia}
            {cardAmbiente}
            {campoInstruccion('mobile')}
            {errorBox}
          </div>
          {/* La MISMA barra de Modelo en mobile (`BarraInferiorMobile`): Calidad y
              Formato arriba, y abajo el contador pegado al Generar. Es el pie de la
              columna de desktop, que es lo que este modo ya dibuja ahí
              (ADR-2026-09-03-modelo-mobile-toma-el-pie-de-desktop). */}
          <BarraInferiorMobile
            calidad={calidad}
            onCalidadChange={setCalidad}
            formatoLabel={getFormatLabel(formatoId)}
            onOpenFormato={() => setPanelLateral('formato')}
            cantidad={cantidad}
            cantidadMax={MAX_ESCENAS}
            onCantidadChange={setCantidad}
            imagenesDeLaTanda={cantidad}
            creditCost={costo}
            // El CTA cuenta ESCENAS y no imágenes: es lo que sale de este modo, y lo que
            // dice el botón de la columna de desktop.
            ctaLabel={`Generar ${cantidad} ${cantidad === 1 ? 'escena' : 'escenas'}`}
            onGenerate={generar}
            falta={falta ?? undefined}
          />
        </div>
      )}
    </div>
  );

  // Lo que en desktop se elige en el panel lateral, en el teléfono se elige en un bottom
  // sheet: los mismos de siempre —`SheetLateral` y el picker de Formato— y con la MISMA
  // grilla de ambientes, a dos columnas en vez de tres. Productos no tiene sheet: es el
  // paso 1, una pantalla entera.
  const sheetsMobile = (
    <>
      <SheetLateral
        open={panelLateral === 'ambiente'}
        onClose={() => setPanelLateral(null)}
        titulo="Ambiente"
      >
        {bloqueAmbientes(2)}
      </SheetLateral>
      <FormatPicker
        open={panelLateral === 'formato'}
        selectedId={formatoId}
        onChange={setFormatoId}
        onClose={() => setPanelLateral(null)}
      />
    </>
  );

  const tituloPanel =
    panelLateral === 'productos'
      ? 'Productos'
      : panelLateral === 'ambiente'
        ? 'Ambiente'
        : 'Formato';

  return (
    <ProductsContext.Provider value={productosCatalogo}>
      <ProductsLoadedContext.Provider value={productosCargados}>
        <ProductEditContext.Provider value={productEditContextValue}>
          {!esEscritorio && pantallaMobile}

          {/* ── DESKTOP: el marco de las pantallas de modo ─────────────────── */}
          {/* La MISMA geometría que "Mostrar cómo funciona" y los modos-sección
              (Automático): a ras del shell —`inset-y-0 right-0`, sin marco flotante ni
              borde—, sobre el gris de la app y redondeado sólo del lado de la sidebar. La
              tarjeta blanca alrededor de las dos columnas se sacó porque son dos cajas
              claras, una adentro de la otra
              (ADR-2026-09-03-el-modo-no-lleva-tarjeta-alrededor-de-las-columnas).

              `lg:pt-[52px]` es el aire de arriba de las pantallas del shell — el mismo que
              deja Automático, y lo que alinea el techo de la columna con el suyo. */}
          {esEscritorio && (
          <div
            className="hidden md:flex fixed inset-y-0 right-0 z-30 bg-surface-mobile rounded-tl-[26px] rounded-bl-[26px] flex-col lg:pt-[52px]"
            style={{ left: sidebarWidth }}
          >
            <div
              ref={areaRef}
              // `flex-row-reverse` + `justify-end`: la configuración a la IZQUIERDA y el
              // DOM con la presentación primero, como en los otros modos.
              //
              // `FILA_MODO_REDISENO`: 52px de sangría a cada lado, el mismo aire que hay
              // arriba, en vez del 90% centrado que dejaba ~104px de un lado y la columna
              // sin llegar al borde (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde).
              className={`relative flex-1 min-h-0 px-8 pb-6 flex flex-row-reverse justify-end gap-6 items-stretch ${FILA_MODO_REDISENO}`}
            >
              {columnaDerecha}
              {/* `paso === 'producto'` y no `!== 'configurar'`: `resultados` es de
                  mobile, y girando el teléfono a desktop con esa pantalla abierta la
                  fila tiene que mostrar la configuración —que es lo que la acompaña—,
                  no volver a la grilla de productos. */}
              {paso === 'producto' ? columnaProducto : columnaConfiguracion}

              <PanelLateralModo
                abierto={panelLateral !== null}
                onCerrar={() => setPanelLateral(null)}
                titulo={tituloPanel}
                maxAncho={ANCHO_PANEL_LATERAL}
                offsetIzquierdo={offsetPanelLateral}
                contenedorRef={areaRef}
              >
                {panelLateral === 'productos'
                  ? panelProductos
                  : panelLateral === 'ambiente'
                    ? panelAmbiente
                    : panelFormato}
              </PanelLateralModo>
            </div>
          </div>
          )}

          {!esEscritorio && sheetsMobile}

          {/* La generación se lleva la pantalla, como en Automático en el teléfono: el
              MISMO `LoadingAnimation` de toda la app —marquee de consejos, barra y
              sonido— y, al terminar, la pantalla de resultados. En desktop no se monta:
              allá la tanda late en la columna de la derecha, que es justo lo que un
              overlay taparía (mismo criterio que ADR-2026-08-20-tanda-en-el-panel).

              `durationMs` es sólo el paceo de la barra: el corte lo da `completeNow`
              cuando la respuesta del backend llega (bien o mal), y recién ahí se pasa a
              los resultados. */}
          {!esEscritorio && generandoMobile && (
            <LoadingAnimation
              message="Armando el ambiente..."
              subMessage="Esto puede tardar un par de minutos"
              durationMs={DURACION_GENERACION_MS}
              completeNow={completeNow}
              onCompleted={() => {
                setGenerandoMobile(false);
                setPaso('resultados');
              }}
            />
          )}

          <SubscriptionLimitModal
            isOpen={limitModal !== null}
            type={limitModal ?? 'plan-limit-reached'}
            onClose={() => setLimitModal(null)}
          />

          {/* Alta de producto desde la tarjeta "Subir nuevo producto" de la grilla. */}
          <SubirProductoDrawer
            open={subirProductoAbierto}
            onClose={() => setSubirProductoAbierto(false)}
            userCompanyId={companyId}
            brandId={brandId}
            rubro={rubro}
            onProductsRefreshed={cargarProductos}
          />
        </ProductEditContext.Provider>
      </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
  );
}
